<template>
    <div class="p-2 ctc-employee-page" style="min-height: 400px;">
        <!-- Summary Cards (matching dashboard metric card styling) -->
            <div class="mb-4">
                <h4 class="ctc-dashboard-title mb-1">Employee Management</h4>
                <div class="ctc-dashboard-subtitle">View and manage employee information for CTC calculations</div>
            </div>
        <div class="row mb-4">
            <!-- Total Employees -->
            <div class="col-md-4 col-lg-3 mb-3">
                <div class="card h-100 dashboard-card w-100">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-start mb-2">
                            <span class="card-metric-title">Total Employees</span>
                            <div class="icon-badge bg-soft-blue text-blue">
                                <b-icon icon="people-fill"></b-icon>
                            </div>
                        </div>
                        <h4 class="card-metric-value mb-0">{{ totalEmployeesCount }}</h4>
                    </div>
                </div>
            </div>

            <!-- Active Employees -->
            <div class="col-md-4 col-lg-3 mb-3">
                <div class="card h-100 dashboard-card w-100">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-start mb-2">
                            <span class="card-metric-title">Active Employees</span>
                            <div class="icon-badge bg-soft-green text-green">
                                <b-icon icon="person-check-fill"></b-icon>
                            </div>
                        </div>
                        <h4 class="card-metric-value mb-0">{{ activeEmployeesCount }}</h4>
                    </div>
                </div>
            </div>
        </div>

<!-- The search field, record counter and pager below are supplied by
             <common-list> itself, so no separate search box is rendered here. -->

        <!-- Employee Table Card -->
        <!-- <div class="card employee-table-card ctc-employee-list-wrap">
            <div class="card-body p-0"> -->
                <!-- Rendered by the shared <common-list> (DemoList.vue), which is
                     used across the product and is deliberately not modified. It
                     supplies the search field, record counter, avatar-prefixed name
                     cell, sorting and pagination that this card's design calls for.
                     `company_options` must name every default column, otherwise
                     DemoList renders the column but leaves its header blank. -->
                <div v-if="loading" class="text-center py-5">
                    <b-spinner small label="Loading..." class="text-teal"></b-spinner>
                    <span class="ml-2 text-muted">Loading employees...</span>
                </div>
                <common-list
                    v-else
                    :key="`ctc-employees-${employeeListKey}`"
                    :employees="employees"
                    :default_column="listDefaultColumn"
                    :appended_column="listAppendedColumn"
                    :company_options="listCompanyOptions"
                    primary_key="employee_id"
                    :is_action="canManage ? 1 : 0"
                    v-on:childToParent="onEmployeeListEvent"
                ></common-list>
            <!-- </div>
        </div> -->

        <!-- Edit Attributes Modal -->
        <b-modal 
            id="edit-attributes-modal" 
            centered 
            hide-header 
            hide-footer
            modal-class="custom-ctc-modal"
        >
            <div class="custom-modal-content" v-if="selectedEmployee">
                <!-- Header -->
                <div class="custom-modal-header text-center position-relative pb-3">
                    <h5 class="modal-title-custom font-weight-bold">Manage CTC Attributes</h5>
                    <p class="modal-subtitle-custom text-muted mb-0">
                        Setting custom attributes for {{ selectedEmployee.name }}<template v-if="selectedEmployee.employee_code"> ({{ selectedEmployee.employee_code }})</template>
                    </p>
                    <button type="button" class="close-btn-custom" @click="$bvModal.hide('edit-attributes-modal')">
                        <b-icon icon="x"></b-icon>
                    </button>
                </div>

                <!-- Body wrapper (white card container) -->
                <div class="custom-modal-card-body mt-3">
                    <div v-if="ctcAttributes.length === 0" class="text-center py-4 text-muted">
                        No CTC custom attributes defined.
                    </div>

                    <div v-for="attr in currentEmployeeAttributes" :key="attr.id" class="form-group mb-4">
                        <label class="custom-field-label font-weight-bold d-block mb-2">{{ attr.label }}</label>

                        <!-- Multi-select via checkboxes (Type 8) -->
                        <div v-if="attr.type == 8" class="custom-checkbox-container p-3" style="max-height: 200px; overflow-y: auto;">
                            <b-form-checkbox-group v-model="editForm[attr.id]" :options="getAttributeOptions(attr)" name="checkbox-options" stacked></b-form-checkbox-group>
                        </div>

                        <!-- Single-select via dropdown (Type 7 or others) -->
                        <b-form-select v-else v-model="editForm[attr.id]" :options="getAttributeOptions(attr)" class="custom-select-field">
                            <template #first>
                                <b-form-select-option :value="null">-- Select Option --</b-form-select-option>
                            </template>
                        </b-form-select>
                    </div>
                </div>

                <!-- Footer / Action Buttons -->
                <div class="custom-modal-footer d-flex justify-content-center mt-4 pt-2">
                    <!-- Same pair as the Annual CTC Reports panel: the secondary
                         action wears "Upload Payment Data" (.custom-wizard-btn),
                         the primary action wears "Generate Projection" (plain
                         .btn-secondary). Both are defined globally in
                         resources/sass/atlhr.scss, so neither is restyled here. -->
                    <button class="btn btn-primary custom-wizard-btn mr-3" @click="$bvModal.hide('edit-attributes-modal')">Cancel</button>
                    <button class="btn btn-secondary" @click="saveAttributes">OK</button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import axios from 'axios';
import ctcOverlay from '../js/ctcOverlay';

export default {
    mixins: [ctcOverlay],
    name: 'CtcEmployee',
    watch: {
        saving(newVal) {
            this.ctcOverlay(newVal);
        },
        // `loading` deliberately does NOT raise the page-level overlay: the table
        // body renders its own spinner row, and emitting here showed two at once.
        //
        // The former currentPage / perPage / searchQuery watchers refetched from
        // the server on every paging or keystroke. <common-list> now does all of
        // that in the browser against the full set, so they have been removed.
    },
    data() {
        return {
            // Row-menu action id for "Edit Attributes". Kept non-zero so it can
            // be told apart from DemoList's plain row-click event.
            EDIT_ATTRIBUTES_ACTION: 1,
            employees: [],
            ctcAttributes: [],
            loading: false,
            saving: false,
            selectedDepartment: '',
            selectedEmployee: null,
            currentEmployeeAttributes: [],
            editForm: {},

            totalRows: 0,
            totalEmployeesCount: 0,
            activeEmployeesCount: 0,

            // --- <common-list> configuration -------------------------------
            // Order here is the column order on screen. `name` must stay in
            // default_column: that is the only key DemoList renders with the
            // employee avatar, which this card's design shows.
            listDefaultColumn: ['name', 'employee_code', 'department'],
            listAppendedColumn: [],
            // Every default column needs a matching entry here or DemoList
            // renders an empty <th> for it. `options` is required too: the
            // filter sidebar reads `options.length` without guarding it.
            listCompanyOptions: [
                { label: 'name',          type: 'text', options: [] },
                { label: 'employee_code', type: 'text', options: [] },
                { label: 'department',    type: 'text', options: [] },
            ],
            employeeListKey: 0,
        }
    },
    computed: {
        canManage() {
            return !!(this.$acl && this.$acl['cost-to-company'] == 2);
        }
    },
    created() {
        this.fetchData();
    },
    methods: {
        async fetchData() {
            this.loading = true;
            try {
                // DemoList searches, sorts and paginates client-side, so the whole
                // set is fetched in one call rather than a page at a time.
                const response = await axios.get('/cost-to-company/employees', {
                    params: {
                        page: 1,
                        per_page: 100000,
                    }
                });
                if (response.data) {
                    const rows = Array.isArray(response.data.data) ? response.data.data : [];
                    // DemoList renders its row menu from an `actions` array and
                    // reports the chosen entry back through childToParent.
                    this.employees = rows.map(emp => ({
                        ...emp,
                        department: emp.department || 'N/A',
                        // `value` must not be 0: DemoList also reports plain row
                        // clicks with action_id 0, so 0 would be indistinguishable
                        // from picking this menu entry.
                        actions: this.canManage ? [{ label: 'Edit Attributes', value: this.EDIT_ATTRIBUTES_ACTION }] : [],
                    }));
                    this.employeeListKey++;
                    this.totalRows = response.data.total || 0;
                    this.totalEmployeesCount = response.data.total_employees_count || 0;
                    this.activeEmployeesCount = response.data.active_employees_count || 0;
                    this.ctcAttributes = Array.isArray(response.data.ctc_attributes) ? response.data.ctc_attributes : [];
                }
            } catch (error) {
                console.error("Error fetching employees:", error);
                this.$bvToast.toast('Failed to load employees', { variant: 'danger' });
            } finally {
                this.loading = false;
            }
        },
        // <common-list> reports row clicks and row-menu picks here. It sends the
        // primary key plus the chosen action id; selection events (no row id)
        // are ignored since this list has no checkboxes. Only the "Edit
        // Attributes" menu entry opens the modal — a bare row click reports
        // action_id 0 and is ignored.
        onEmployeeListEvent(value) {
            const employeeId = value && value['employee_id'];
            if (!employeeId) return;
            if (Number(value['action_id']) !== this.EDIT_ATTRIBUTES_ACTION) return;

            const employee = this.employees.find(emp => emp.employee_id == employeeId);
            if (employee) {
                this.openEditModal(employee);
            }
        },
        getAttributeOptions(attr) {
            if (!attr || !attr.options) return [];
            return attr.options.map(opt => ({
                value: opt.id,
                text: opt.label
            }));
        },
        async openEditModal(emp) {
            if (!this.$acl || this.$acl['cost-to-company'] < 2) {
                return;
            }
            this.selectedEmployee = emp;
            this.editForm = {};
            this.currentEmployeeAttributes = [];
            this.$bvModal.show('edit-attributes-modal');

            try {
                const response = await axios.get(`/cost-to-company/employees/${emp.employee_id}/attributes`);
                this.currentEmployeeAttributes = response.data.attributes;

                this.currentEmployeeAttributes.forEach(attr => {
                    this.$set(this.editForm, attr.id, attr.current_value);
                });
            } catch (error) {
                console.error("Error fetching employee attributes:", error);
                this.$bvToast.toast('Failed to load employee attributes', { variant: 'danger' });
                this.$bvModal.hide('edit-attributes-modal');
            }
        },
        async saveAttributes(bvModalEvent) {
            if (bvModalEvent && typeof bvModalEvent.preventDefault === 'function') {
                bvModalEvent.preventDefault();
            }
            if (!this.$acl || this.$acl['cost-to-company'] < 2) {
                return;
            }
            // Nothing to persist (employee has no CTC attributes defined) — close
            // quietly instead of posting an empty payload the API rejects with 422.
            if (Object.keys(this.editForm).length === 0) {
                this.$bvModal.hide('edit-attributes-modal');
                return;
            }
            this.saving = true;
            try {
                await axios.post(`/cost-to-company/employees/${this.selectedEmployee.employee_id}/attributes`, {
                    attributes: this.editForm
                });
                this.$bvToast.toast('Attributes updated successfully', { title: `Success`, variant: 'success', solid: true });
                this.$bvModal.hide('edit-attributes-modal');
                this.fetchData();
            } catch (error) {
                console.error("Error saving attributes:", error);
                this.$bvToast.toast('Failed to save attributes', { variant: 'danger' });
            } finally {
                this.saving = false;
            }
        },
        // Employee photos are served from public/avatar/{company_id}/{pic} (the same
        // path the main employee list uses). 'no-image.jpg' and 'demo.jpg' are the
        // seeded placeholders, so those fall through to the coloured initials avatar
        // rather than rendering a generic grey silhouette.
        hasEmployeePic(emp) {
            return !!emp.pic && emp.pic !== 'no-image.jpg' && emp.pic !== 'demo.jpg';
        },
        employeePicUrl(emp) {
            return this.$api_url + '/avatar/' + emp.company_id + '/' + emp.pic;
        },
        getAvatarColor(name) {
            if (!name) return '#cbd5e1';
            const colors = [
                '#d97706', // Warm Amber/Orange
                '#059669', // Emerald/Green
                '#01524B', // Brand Dark Green
                '#0892A4', // Brand Cyan
                '#2563eb', // Blue
                '#7c3aed'  // Purple
            ];
            // Deterministic hash based on name
            let hash = 0;
            for (let i = 0; i < name.length; i++) {
                hash = name.charCodeAt(i) + ((hash << 5) - hash);
            }
            const index = Math.abs(hash) % colors.length;
            return colors[index];
        },
        getInitial(name) {
            if (!name) return '';
            return name.trim().charAt(0).toUpperCase();
        }
    }
}
</script>

<style scoped>
/* Metric card styling matching dashboard */
.dashboard-card w-100 .card-body {
    padding: 1rem;
}
.icon-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    font-size: 1rem;
}
.bg-soft-blue {
    background-color: #eff6ff;
}
.text-blue {
    color: #3b82f6;
}
.bg-soft-green {
    background-color: #f0fdf4;
}
.text-green {
    color: #22c55e;
}

/* The search box (.search-input-group) is styled centrally in
   resources/sass/_ctc.scss so every CTC list screen shares one definition. */
.custom-select-filter {
    border-radius: 10px;
    border: 1px solid #E8E8E8;
    background-color: #ffffff;
    color: #34444C;
    font-size: 0.9rem;
    font-weight: 500;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    padding: 0 12px;
}
.custom-select-filter:focus {
    border-color: #cbd5e1;
    box-shadow: none;
}

/* Table Card styling */
.employee-table-card {
    border: 1px solid #E8E8E8;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
    overflow: hidden;
    background-color: #ffffff;
}
.employee-table-card .table {
    border-collapse: separate;
    border-spacing: 0;
}
.employee-table-card thead {
    background-color: #01524B !important;
}
.employee-table-card thead th {
    color: #ffffff !important;
    font-weight: 600;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 14px 20px;
    border: none;
}
/* Column separators in the teal header row */
.employee-table-card thead th + th {
    border-left: 1px solid rgba(255, 255, 255, 0.25);
}
.employee-row {
    transition: background-color 0.15s ease-in-out;
}
.employee-row:hover {
    background-color: #f8fafc !important;
    cursor: pointer;
}
.employee-code-cell {
    font-weight: 700;
    color: #34444C;
    vertical-align: middle !important;
    padding: 14px 20px !important;
    font-size: 0.9rem;
}
.employee-name-cell {
    vertical-align: middle !important;
    padding: 14px 20px !important;
}
.employee-name {
    font-weight: 600;
    color: #0f172a;
    font-size: 0.9rem;
}
.employee-dept-cell {
    vertical-align: middle !important;
    padding: 14px 20px !important;
    color: #475569;
    font-size: 0.9rem;
    font-weight: 500;
}

/* Avatar Circle */
.avatar-circle {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.85rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* Dropdown actions */
.action-dropdown ::v-deep .btn-link {
    padding: 0;
    border: none;
    text-decoration: none;
}

/* Custom Modal Styles */
::v-deep .custom-ctc-modal .modal-content {
    border-radius: 20px !important;
    border: none !important;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
    padding: 24px !important;
}

.modal-title-custom {
    color: #212529;
    font-weight: 700 !important;
    font-size: 1.25rem;
}

.modal-subtitle-custom {
    font-size: 0.85rem;
    color: #727272 !important;
}

/* White card body container */
.custom-modal-card-body {
    background-color: #ffffff;
    border: 1px solid #f1f5f9;
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
    max-height: 450px;
    overflow-y: auto;
}

/* Scrollbar styling for card body */
.custom-modal-card-body::-webkit-scrollbar {
    width: 6px;
}
.custom-modal-card-body::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 10px;
}
.custom-modal-card-body::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
}

.custom-checkbox-container {
    border: 1.5px solid #a3cbd6;
    border-radius: 10px;
    background-color: #ffffff;
}

/* Footer buttons are the system pair (.custom-wizard-btn / plain .btn-secondary)
   copied from the Annual CTC Reports panel — see the template. */

/* Page header (.ctc-dashboard-title / .ctc-dashboard-subtitle) and pagination
   are styled centrally in resources/sass/_ctc.scss. */

/* The <common-list> table itself — header bar, sort arrows, body rows, zebra
   striping and the suppressed column picker / filter trigger — is styled in
   resources/sass/_ctc.scss under `.ctc-employee-page`, shared with the Annual
   Reports and Audit Logs lists so the three render one identical table. Only
   this screen's own additions live here. */

/* Row-menu icon. DemoList picks the icon from the action's label and falls back
   to a gear for anything it doesn't know, which is what "Edit Attributes" gets.
   It can't be edited, so the gear is swapped here: its paths are hidden and the
   Bootstrap `pencil` outline is masked over the same box, which keeps the
   icon's 1em size and `currentColor` fill. */
.ctc-employee-page ::v-deep .action-dropdown-menu svg.bi-gear > * {
    display: none;
}
.ctc-employee-page ::v-deep .action-dropdown-menu svg.bi-gear {
    background-color: currentColor;
    -webkit-mask-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z'/%3E%3C/svg%3E");
    mask-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z'/%3E%3C/svg%3E");
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;
    -webkit-mask-size: 100% 100%;
    mask-size: 100% 100%;
}
</style>
